import type { ImapCatalogSetup } from "@/types/connectors.ts";
import type { ImapOauthProvider, ImapPresetLookupResp } from "@/types/imap.ts";

/** What the OAuth mailboxes are called on their own tiles, which is where these addresses belong. */
const OAUTH_TILE_NAMES: Record<ImapOauthProvider, string> = {
  google: "Gmail",
  microsoft: "Outlook",
};

/** A preset tile's own identity: which preset it stands for, and what to show. */
export type ImapPresetTile = ImapCatalogSetup & { name: string };

/**
 * Why the typed address does not belong on this tile, if it does not.
 *
 * The connect endpoint says the same thing, but only after spending one of the
 * ten attempts an account gets each hour. Answering from the preset lookup
 * instead costs nothing and keeps a mistyped tile from burning a retry the user
 * may need for a genuinely wrong password.
 *
 * Shared because it is the one piece of this form's copy that has to match
 * across clients: the same address on the same tile should be refused in the
 * same words on the phone as on the web.
 */
export const imapPresetGuardCopy = (
  hint: ImapPresetLookupResp,
  tile: ImapPresetTile,
): string | undefined => {
  switch (hint.kind) {
    case "preset":
      return hint.presetId === tile.presetId
        ? undefined
        : `That looks like a ${hint.name} address. Connect it from the ${hint.name} option instead.`;
    case "oauth_provider": {
      const oauth = OAUTH_TILE_NAMES[hint.provider];
      return `${oauth} mailboxes connect with their own sign-in — use the ${oauth} tile instead.`;
    }
    case "unknown_provider":
      return `That is not a ${tile.name} address. Use "Other email inbox" to enter its mail servers yourself.`;
    default:
      return undefined;
  }
};

/**
 * What the generic tile tells the user about the address they typed.
 *
 * Unlike {@link imapPresetGuardCopy} this never blocks the submit: the generic
 * tile connects a known provider from the address alone, and an unknown one
 * once the servers are filled in under Advanced.
 */
export const imapLookupHintCopy = (hint: ImapPresetLookupResp): string | undefined => {
  switch (hint.kind) {
    case "preset":
      return `${hint.name}: ${hint.passwordHelp}`;
    case "unknown_provider":
      return "We don't know this provider's mail servers. Enter them under Advanced.";
    case "oauth_provider": {
      const tile = OAUTH_TILE_NAMES[hint.provider];
      return `${tile} mailboxes connect with their own sign-in — use the ${tile} tile instead.`;
    }
    default:
      return undefined;
  }
};

/**
 * Whether an address the lookup answered for is still the one on screen.
 *
 * The lookup trails the field — it runs when the address is committed, not on
 * every keystroke — so a result can describe an address the user has already
 * corrected. Saying "that looks like an AT&T address" under a fixed address is
 * worse than saying nothing, and on a preset tile it also blocks a mailbox that
 * would connect fine.
 */
export const imapLookupIsCurrent = (lookedUp: string | undefined, typed: string): boolean =>
  (lookedUp ?? "").trim().toLowerCase() === typed.trim().toLowerCase();
